'use strict';

/**
 * NACRE Wave 2.1.1 — KDF primitive
 *
 * Two key-derivation primitives used throughout the vault system:
 *
 *   1. argon2id(password, salt, profile?) — memory-hard password KDF
 *      - Used to derive vault unlock keys from passwords and PINs
 *      - Slow on purpose: 32-64 MB memory, multiple iterations
 *      - Pure JS via @noble/hashes (no native bindings)
 *
 *   2. hkdfSubkey(master, label, length) — fast deterministic subkey derivation
 *      - Used to derive per-module vault keys from a single master
 *      - Used to derive per-capability subkeys from a vault key (delegation)
 *      - Used to derive feature-specific subkeys (encryption vs signing)
 *      - The `label` provides domain separation: same master + different
 *        labels yields cryptographically independent subkeys
 *      - Versioned labels (e.g. "oyster:vault:messages:v1") let us rotate
 *        subkeys without changing the master
 *
 * Both functions are deterministic and side-effect-free. They take a
 * `profile` for tunable parameters so that signup, login, and recovery
 * use the same parameters but tests can run with cheap defaults.
 *
 * Profiles:
 *   - DESKTOP: 64 MB / 3 iters / 1 lane     ← server + desktop browsers
 *   - MOBILE:  32 MB / 3 iters / 1 lane     ← detected mobile clients
 *   - FAST_TEST: 8 KB / 1 iter / 1 lane     ← unit tests only, NOT for prod
 *
 * On a 2026-era laptop, DESKTOP takes ~250-400 ms and MOBILE takes ~150-250 ms.
 * On a 2015 MacBook Pro, DESKTOP takes ~3 s and MOBILE takes ~1.5 s. Production
 * server hardware will be much faster than dev hardware.
 */

const { argon2id: nobleArgon2id } = require('@noble/hashes/argon2.js');
const { hkdf: nobleHkdf } = require('@noble/hashes/hkdf.js');
const { sha512 } = require('@noble/hashes/sha2.js');

const TEXT_ENCODER = new TextEncoder();

/**
 * Argon2id profiles. `m` is in KiB.
 *
 * Per Argon2 spec recommendations + 2026-era hardware:
 *   - DESKTOP: 64 MB memory is "strong" — well above the OWASP minimum of
 *     19 MB. Brute force resistance ~2^33 attempts per CPU-year.
 *   - MOBILE: 32 MB is "good" — at OWASP recommended for mobile. Brute force
 *     resistance ~2^32 attempts per CPU-year.
 *   - FAST_TEST: explicitly insecure for tests only. Never use in production.
 */
const KDF_PROFILES = {
  DESKTOP: { m: 64 * 1024, t: 3, p: 1, dkLen: 32 },  // 64 MB
  MOBILE:  { m: 32 * 1024, t: 3, p: 1, dkLen: 32 },  // 32 MB
  FAST_TEST: { m: 8, t: 1, p: 1, dkLen: 32 },        // 8 KB — INSECURE, tests only
};

const FAST_TEST_PROFILE = KDF_PROFILES.FAST_TEST;

/**
 * Coerce a string or Uint8Array to a Uint8Array.
 */
function toBytes(value, name) {
  if (value instanceof Uint8Array) return value;
  if (typeof value === 'string') return TEXT_ENCODER.encode(value);
  throw new TypeError('Invalid ' + name + ': must be string or Uint8Array');
}

/**
 * Derive a key from a password using Argon2id.
 *
 * @param {string|Uint8Array} password
 * @param {string|Uint8Array} salt — must be at least 16 bytes
 * @param {object} [profile] — { m, t, p, dkLen } — defaults to DESKTOP
 * @returns {Uint8Array} derived key (length = profile.dkLen, default 32)
 * @throws if salt is shorter than 16 bytes or password/salt have wrong types
 */
function argon2id(password, salt, profile) {
  const opts = profile || KDF_PROFILES.DESKTOP;
  const pwBytes = toBytes(password, 'password');
  const saltBytes = toBytes(salt, 'salt');
  if (saltBytes.length < 16) {
    throw new Error('Invalid salt: must be at least 16 bytes (got ' + saltBytes.length + ')');
  }
  return nobleArgon2id(pwBytes, saltBytes, {
    m: opts.m,
    t: opts.t,
    p: opts.p,
    dkLen: opts.dkLen || 32,
  });
}

/**
 * Derive a labeled subkey from a master key via HKDF-SHA512.
 *
 * @param {Uint8Array} master — at least 32 bytes
 * @param {string} label — domain separation tag (e.g., "oyster:vault:messages:v1")
 * @param {number} length — subkey length in bytes (1..64)
 * @returns {Uint8Array} subkey
 *
 * The label is used as HKDF's `info` parameter, which provides cryptographic
 * domain separation. Two different labels with the same master yield
 * mathematically independent subkeys — knowing one tells you nothing about
 * the other.
 *
 * Conventions for labels:
 *   - oyster:vault:<moduleId>:v<N>          per-module vault subkey
 *   - oyster:delegate:<from>:<to>:<scope>:v<N>   delegated capability
 *   - oyster:msg:kek:v<N>                    per-message KEK
 *   - oyster:hw:<moduleId>:v<N>              hardware-bound subkey
 */
function hkdfSubkey(master, label, length) {
  if (!(master instanceof Uint8Array) || master.length < 32) {
    throw new Error('Invalid master: must be Uint8Array of at least 32 bytes');
  }
  if (typeof label !== 'string' || label.length === 0) {
    throw new Error('Invalid label: must be a non-empty string for domain separation');
  }
  if (typeof length !== 'number' || length <= 0 || length > 64) {
    throw new Error('Invalid length: must be 1..64');
  }
  // HKDF: extract step uses an empty salt (we have a strong master), expand
  // step uses the label as `info` to bind the output to its purpose.
  const info = TEXT_ENCODER.encode(label);
  return nobleHkdf(sha512, master, new Uint8Array(0), info, length);
}

module.exports = {
  argon2id,
  hkdfSubkey,
  KDF_PROFILES,
  FAST_TEST_PROFILE,
};
